import React, { useEffect, useState, useMemo } from 'react';
import { useHistoricalData } from '@/api/hooks/useHistoricalData';
import { DataPoint as ApiDataPoint } from '@/api/historical-data/types';
import dayjs from 'dayjs';
import { useSitesWithLatestData } from '@/api/hooks/useSites';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

// Color theme constants
const THEME = {
    // Chart colors
    consumption: {
        line: '#0E7EE4',     // Blue 50
        area: 'rgba(14, 126, 228, 0.1)'
    },
    solar: {
        actual: '#F2AA32',    // Orange 40
        expected: '#F26428'   // Orange (dashed)
    },
    temperature: {
        outdoor: '#109095',   // Teal
        indoor: '#14B0BC'     // Mid Green
    }
};

// Define types for the chart data
interface ChartDataPoint {
    time: string;
    acPower?: number;
    totalPower?: number;
    gridImport?: number;
    solarGeneration?: number;
    expectedSolar?: number;
    outdoorTemp?: number;
    avgIndoorTemp?: number;
}

// Helper function to get date range for API calls
const getDateRange = (selectedDate?: dayjs.Dayjs) => {
    // If a date is provided, use it; otherwise use the current date
    const date = selectedDate ? selectedDate.toDate() : new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return {
        start_timestamp: `${year}-${month}-${day}T00:00:00+07:00`,
        end_timestamp: `${year}-${month}-${day}T23:59:59+07:00`,
        tz: 'Asia/Bangkok'
    };
};

// Helper function to generate full day time points (00:00 to 23:59)
const generateFullDayTimePoints = (data: ChartDataPoint[]): ChartDataPoint[] => {
    // Create a map with all existing data points
    const dataMap = new Map<string, ChartDataPoint>();
    data.forEach(point => {
        dataMap.set(point.time, point);
    });

    // Generate all time points for a full day (every 5 minutes)
    const fullDayData: ChartDataPoint[] = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 5) {
            const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            const existingPoint = dataMap.get(timeStr);

            if (existingPoint) {
                fullDayData.push(existingPoint);
            } else {
                fullDayData.push({ time: timeStr });
            }
        }
    }

    return fullDayData;
};


interface EnergyPowerChartProps {
    selectedDate: dayjs.Dayjs;
}

const EnergyPowerChart: React.FC<EnergyPowerChartProps> = ({ selectedDate }) => {
    const [energyChartData, setEnergyChartData] = useState<ChartDataPoint[]>([]);

    // State for legend visibility
    const [legendVisible, setLegendVisible] = useState({
        acPower: true,
        totalPower: true,
        gridImport: true,
        solarGeneration: true,
        expectedSolar: true,
        outdoorTemp: true,
        avgIndoorTemp: true
    });

    // Fetch sites data to get the site ID dynamically
    const { data: sitesData, isLoading: isSitesLoading } = useSitesWithLatestData();
    const siteId = useMemo(() => {
        // Use the first site's ID if available, otherwise fallback to a default
        return sitesData?.data?.[0]?.id || 'dohome-rama2';
    }, [sitesData]);

    // Get date range for API calls
    const dateRange = getDateRange(selectedDate);

    // API calls for historical data
    const { data: acPowerData, isLoading: isAcPowerLoading } = useHistoricalData({
        device_id: 'ac_subsystem',
        datapoints: ['power'],
        ...dateRange
    });

    const { data: solarData, isLoading: isSolarLoading } = useHistoricalData({
        device_id: 'solar_subsystem',
        datapoints: ['power', 'expected_power', 'grid_power'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    const { data: outdoorTempData, isLoading: isOutdoorTempLoading } = useHistoricalData({
        device_id: siteId,
        datapoints: ['outdoor_temperature'],
        resampling: '5min',
        ...dateRange
    });

    const { data: indoorTempData, isLoading: isIndoorTempLoading } = useHistoricalData({
        device_id: 'zone_main',
        datapoints: ['avg_room_temperature'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    // Check if any data is still loading
    const isLoading = isAcPowerLoading || isSolarLoading || isOutdoorTempLoading || isIndoorTempLoading || isSitesLoading;

    // Process data for Energy chart
    useEffect(() => {
        if (!isAcPowerLoading && !isSolarLoading && !isOutdoorTempLoading && !isIndoorTempLoading &&
            acPowerData && solarData && outdoorTempData && indoorTempData) {
            try {
                console.log('Processing energy chart data');

                // Create a map to store data points by timestamp
                const dataMap = new Map<string, ChartDataPoint>();

                // Process AC power data (consumption)
                const acPowerArray = Array.isArray(acPowerData) ? acPowerData :
                    (acPowerData.data && Array.isArray(acPowerData.data)) ? acPowerData.data :
                        (acPowerData.data && acPowerData.data.data) ? acPowerData.data.data : [];

                if (acPowerArray.length > 0 && acPowerArray[0].values) {
                    acPowerArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        dataMap.set(time, {
                            time,
                            acPower: point.value
                        });
                    });
                }

                // Process solar data
                const solarDataArray = Array.isArray(solarData) ? solarData :
                    (solarData.data && Array.isArray(solarData.data)) ? solarData.data :
                        (solarData.data && solarData.data.data) ? solarData.data.data : [];

                if (solarDataArray.length > 0) {
                    // Process solar power data
                    const solarPowerData = solarDataArray.find(item => item.datapoint === 'power');
                    if (solarPowerData && solarPowerData.values) {
                        solarPowerData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.solarGeneration = point.value;
                            // Calculate total power if grid import is available
                            if (existingPoint.gridImport !== undefined && existingPoint.solarGeneration !== undefined) {
                                existingPoint.totalPower = existingPoint.solarGeneration + existingPoint.gridImport;
                            }
                            dataMap.set(time, existingPoint);
                        });
                    }

                    // Process expected solar power data
                    const expectedSolarData = solarDataArray.find(item => item.datapoint === 'expected_power');
                    if (expectedSolarData && expectedSolarData.values) {
                        expectedSolarData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.expectedSolar = point.value;
                            dataMap.set(time, existingPoint);
                        });
                    }

                    // Process grid power data
                    const gridPowerData = solarDataArray.find(item => item.datapoint === 'grid_power');
                    if (gridPowerData && gridPowerData.values) {
                        gridPowerData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.gridImport = point.value;
                            // Calculate total power = solar generation + grid import
                            if (existingPoint.solarGeneration !== undefined && existingPoint.gridImport !== undefined) {
                                existingPoint.totalPower = existingPoint.solarGeneration + existingPoint.gridImport;
                            }
                            dataMap.set(time, existingPoint);
                        });
                    }
                }

                // Process outdoor temperature data
                const outdoorTempArray = Array.isArray(outdoorTempData) ? outdoorTempData :
                    (outdoorTempData.data && Array.isArray(outdoorTempData.data)) ? outdoorTempData.data :
                        (outdoorTempData.data && outdoorTempData.data.data) ? outdoorTempData.data.data : [];

                if (outdoorTempArray.length > 0 && outdoorTempArray[0].values) {
                    outdoorTempArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        const existingPoint = dataMap.get(time) || { time };
                        existingPoint.outdoorTemp = point.value;
                        dataMap.set(time, existingPoint);
                    });
                }

                // Process indoor temperature data
                const indoorTempArray = Array.isArray(indoorTempData) ? indoorTempData :
                    (indoorTempData.data && Array.isArray(indoorTempData.data)) ? indoorTempData.data :
                        (indoorTempData.data && indoorTempData.data.data) ? indoorTempData.data.data : [];

                if (indoorTempArray.length > 0 && indoorTempArray[0].values) {
                    indoorTempArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        const existingPoint = dataMap.get(time) || { time };
                        existingPoint.avgIndoorTemp = point.value;
                        dataMap.set(time, existingPoint);
                    });
                }

                // Convert map to array and sort by time
                const chartData = Array.from(dataMap.values()).sort((a, b) => {
                    return a.time.localeCompare(b.time);
                });

                // Generate full day time points if needed (for x-axis consistency)
                const fullDayData = generateFullDayTimePoints(chartData);

                console.log('Energy chart data processed:', fullDayData);
                setEnergyChartData(fullDayData);
            } catch (error) {
                console.error('Error processing energy chart data:', error);
                setEnergyChartData([]);
            }
        }
    }, [acPowerData, solarData, outdoorTempData, indoorTempData, isAcPowerLoading, isSolarLoading, isOutdoorTempLoading, isIndoorTempLoading, dateRange.start_timestamp, dateRange.end_timestamp]);

    // Custom tooltip formatter for Recharts
    const formatTooltip = (value: any, name: string, props: any) => {
        if (value !== undefined && value !== null) {
            const formattedValue = typeof value === 'number' ? value.toFixed(1) : value;
            return [formattedValue, name];
        }
        return [null, name];
    };

    // Custom label formatter for time display
    const formatLabel = (label: string) => {
        return label;
    };

    // X-axis tick filter
    const getXAxisTicks = (data: ChartDataPoint[]) => {
        return data.map(item => item.time).filter(time =>
            time === '06:00' || time === '12:00' || time === '18:00'
        );
    };

    // Energy Chart Component
    const EnergyChart = useMemo(() => {
        return (
            <div className="w-full h-full relative">
                <div className="absolute -top-2 left-0 right-0 z-10 flex justify-center">
                    <div className="flex flex-wrap gap-x-3 gap-y-0 text-[10px] text-gray-600 bg-white/90 px-1 py-0.5 rounded">
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.acPower ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, acPower: !prev.acPower }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.consumption.line }}></div>
                            <span>A/C Power</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.totalPower ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, totalPower: !prev.totalPower }))}
                        >
                            <div className="w-2 h-2 rounded-full bg-black"></div>
                            <span>Total Power</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.gridImport ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, gridImport: !prev.gridImport }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7D3BBF' }}></div>
                            <span>Grid Import</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.solarGeneration ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, solarGeneration: !prev.solarGeneration }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.solar.actual }}></div>
                            <span>Solar Power</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.expectedSolar ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, expectedSolar: !prev.expectedSolar }))}
                        >
                            <div className="w-2 h-1 rounded-full" style={{ backgroundColor: THEME.solar.expected, backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, white 2px, white 4px)' }}></div>
                            <span>Expected Solar</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.outdoorTemp ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, outdoorTemp: !prev.outdoorTemp }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.temperature.outdoor }}></div>
                            <span>Outdoor Temp</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!legendVisible.avgIndoorTemp ? 'opacity-50' : ''}`}
                            onClick={() => setLegendVisible(prev => ({ ...prev, avgIndoorTemp: !prev.avgIndoorTemp }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.temperature.indoor }}></div>
                            <span>Indoor Temp</span>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={energyChartData}
                        margin={{ top: 15, right: 10, left: 10, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="2 2" stroke="#E0E0E0" horizontal={true} vertical={false} />
                        <XAxis
                            dataKey="time"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#666' }}
                            ticks={getXAxisTicks(energyChartData)}
                            height={12}
                        />
                        <YAxis
                            yAxisId="power"
                            domain={[0, 1500]}
                            ticks={[0, 500, 1000, 1500]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={18}
                        />
                        <YAxis
                            yAxisId="temp"
                            orientation="right"
                            domain={[15, 50]}
                            ticks={[15, 20, 25, 30, 35, 40, 45, 50]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={15}
                        />
                        <text x="2%" y="95%" fontSize={10} fill="#666" textAnchor="start">
                            Power (kW)
                        </text>
                        <text x="98%" y="95%" fontSize={10} fill="#666" textAnchor="end">
                            Temperature (°C)
                        </text>
                        <Tooltip
                            formatter={formatTooltip}
                            labelFormatter={formatLabel}
                            contentStyle={{
                                fontSize: '10px',
                                fontFamily: 'Inter',
                                padding: '4px',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #ccc',
                                borderRadius: '3px'
                            }}
                        />

                        {legendVisible.acPower && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="acPower"
                                stroke={THEME.consumption.line}
                                fill={THEME.consumption.area}
                                strokeWidth={1.5}
                                dot={false}
                                name="A/C Power"
                            />
                        )}
                        {legendVisible.totalPower && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="totalPower"
                                stroke="#000000"
                                fill="rgba(0, 0, 0, 0.1)"
                                strokeWidth={1.5}
                                dot={false}
                                name="Total Power"
                            />
                        )}
                        {legendVisible.gridImport && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="gridImport"
                                stroke="#7D3BBF"
                                fill="rgba(125, 59, 191, 0.1)"
                                strokeWidth={1.5}
                                dot={false}
                                name="Grid Import"
                            />
                        )}
                        {legendVisible.solarGeneration && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="solarGeneration"
                                stroke={THEME.solar.actual}
                                fill="rgba(242, 170, 50, 0.1)"
                                strokeWidth={1.5}
                                dot={false}
                                name="Solar Power"
                            />
                        )}
                        {legendVisible.expectedSolar && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="expectedSolar"
                                stroke={THEME.solar.expected}
                                fill="none"
                                strokeWidth={1.5}
                                strokeDasharray="5 5"
                                dot={false}
                                name="Expected Solar Power"
                            />
                        )}
                        {legendVisible.outdoorTemp && (
                            <Area
                                yAxisId="temp"
                                type="linear"
                                dataKey="outdoorTemp"
                                stroke={THEME.temperature.outdoor}
                                fill="none"
                                strokeWidth={1.5}
                                dot={false}
                                name="Outdoor Temp"
                            />
                        )}
                        {legendVisible.avgIndoorTemp && (
                            <Area
                                yAxisId="temp"
                                type="linear"
                                dataKey="avgIndoorTemp"
                                stroke={THEME.temperature.indoor}
                                fill="none"
                                strokeWidth={1.5}
                                dot={false}
                                name="Indoor Temp"
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        );
    }, [energyChartData, legendVisible]);

    return (
        <div className="h-full relative">
            {isLoading ? (
                <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                    <div className="flex flex-col items-center gap-4">
                        <div className="flex gap-1">
                            <div className="w-2 h-2 bg-primary rounded-full animate-loading-dots"></div>
                            <div className="w-2 h-2 bg-primary rounded-full animate-loading-dots delay-150"></div>
                            <div className="w-2 h-2 bg-primary rounded-full animate-loading-dots delay-300"></div>
                        </div>
                        <div className="text-sm text-gray-600 font-medium">Loading chart data...</div>
                    </div>
                </div>
            ) : null}
            
            {energyChartData.length > 0 ? (
                <div className={`h-full transition-opacity duration-300 ${isLoading ? 'opacity-30' : 'opacity-100'}`}>
                    {EnergyChart}
                </div>
            ) : !isLoading ? (
                <div className="flex items-center justify-center h-full text-[10px] text-gray-400">
                    No data available
                </div>
            ) : null}
        </div>
    );
};

export default EnergyPowerChart;